import React, { useState, useEffect, useRef } from "react";
import guide from "../../assets/images/guidelines.svg";
import uploadIcon from "../../assets/images/upload.svg";
import "react-toastify/dist/ReactToastify.css";
import { toast } from "react-toastify";
import StorageHelper from "../../Utility/StorageHelper";
import { FnAuthenticatedRequest } from "../../Utility/Utils";
import { useHistory } from "react-router-dom";
import ExcelJS from "exceljs";

interface LocationData {
  state: string;
  district: string;
  taluka: string;
  village: string;
  zone: string;
  territory: string;
  stateCode: string;
}

export default function LocationUpload() {
  const [uploadedData, setUploadedData] = useState<LocationData[]>([]);
  const [fileName, setFileName] = useState("");
  const history = useHistory();

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.name !== fileName) {
      try {
        const buffer = await file.arrayBuffer();
        const workbook = new ExcelJS.Workbook();
        await workbook.xlsx.load(buffer);

        const worksheet = workbook.worksheets[0];
        const arrUploadedData: any[] = [];
        worksheet.eachRow((row, rowNumber) => {
          if (rowNumber === 1) return; // Skip header

          const rowData: any = {};
          let hasValidData = false; // Flag to track if the row has valid data

          worksheet.getRow(1).eachCell((cell, colNumber) => {
            const header = cell.text.trim().toLowerCase();
            const value = row.getCell(colNumber).text.trim();

            rowData[header] = value;

            // Check if there is any meaningful data in the row
            if (value && value.trim() !== "") {
              hasValidData = true;
            }
          });

          // Only push the row if it contains valid data (not empty or whitespace-only)
          if (hasValidData) {
            const locationData = {
              zone: rowData["zone"],
              state: rowData["state"],
              territory: rowData["territory"],
              district: rowData["district"],
              taluka: rowData["taluka"],
              village: rowData["village"],
              stateCode: rowData["state code"],
            };

            arrUploadedData.push(locationData);
          }
        });

        if (arrUploadedData.length > 0) {
          setUploadedData(arrUploadedData);
          setFileName(file.name);
        } else {
          toast.error("No data found in the Excel file.");
          setTimeout(() => {
            window.location.reload();
          }, 2000);
        }
      } catch (error) {
        console.error("Error parsing file:", error);
      }
    }
  };

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleRemoveFile = () => {
      setFileName("");
      setUploadedData([]);
    // Reset file input value
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSave = async (data: LocationData[]) => {
    try {
      if (!Array.isArray(data)) {
        throw new Error("Data is not an array");
      }

      const requiredFields = [
        "zone",
        "state",
        "stateCode",
        "territory",
        "district",
        "taluka",
        "village",
      ]; // Always required
      const optionalFields = ["village"]; // Can be empty, no conditions
      const conditionallyRequiredPairs = [
        { base: "village", dependsOn: "taluka" }, // if village is filled, taluka must be too
      ];

      // const isValidText = (text: string) =>
      //   /^[a-zA-Z0-9\s]+$/.test(text.trim());

      const isValidText = (text: string) =>
        /^[a-zA-Z](?:[a-zA-Z0-9.& -]|(?: [a-zA-Z0-9.& -]))*$/.test(text.trim());

      const inputData = data.map((location, index) => {
        const rowNum = index + 1;

        // Check required fields
        for (const field of requiredFields) {
          const value = (location as any)[field];
          if (typeof value !== "string" || value.trim() === "") {
            throw new Error(`"${field}" is required in row ${rowNum}`);
          }
          if (!isValidText(value)) {
            throw new Error(
              `Invalid characters in "${field}" of row ${rowNum}`
            );
          }
        }

        // Check optional fields (only validate if filled)
        for (const field of optionalFields) {
          const value = (location as any)[field];
          if (value && typeof value === "string" && !isValidText(value)) {
            throw new Error(
              `Invalid characters in optional field "${field}" of row ${rowNum}`
            );
          }
        }

        // Check conditional required fields
        for (const pair of conditionallyRequiredPairs) {
          const baseValue = (location as any)[pair.base];
          const dependentValue = (location as any)[pair.dependsOn];

          if (
            typeof baseValue === "string" &&
            baseValue.trim() !== "" &&
            (typeof dependentValue !== "string" || dependentValue.trim() === "")
          ) {
            throw new Error(
              `"${pair.dependsOn}" is required in row ${rowNum} when "${pair.base}" is filled`
            );
          }

          if (
            typeof dependentValue === "string" &&
            dependentValue.trim() !== "" &&
            !isValidText(dependentValue)
          ) {
            throw new Error(
              `Invalid characters in "${pair.dependsOn}" of row ${rowNum}`
            );
          }
        }

        return {
          zone: location?.zone?.trim() || "",
          state: location?.state?.trim() || "",
          territory: location?.territory?.trim() || "",
          district: location?.district?.trim() || "",
          taluka: location?.taluka?.trim() || "",
          village: location?.village?.trim() || "",
          stateCode: location?.stateCode?.trim() || "",
        };
      });

      const authData = JSON.parse(StorageHelper.get("auth") || "{}");
      const authHeader = {
        JWTToken: authData.jwtToken,
        userId: authData.userId,
      };

      const response: any = await FnAuthenticatedRequest(
        "POST",
        "AddLocationData",
        authHeader,
        inputData
      );

      if (response?.success) {
        toast.success("New Entries Added Successfully.", {
          position: toast.POSITION.TOP_CENTER,
          style: { backgroundColor: "#258750", color: "white" },
          autoClose: 3000,
        });

        setTimeout(() => {
          window.location.reload();
        }, 2000);
      } else {
        toast.error(response.error, {
          position: toast.POSITION.TOP_CENTER,
          autoClose: 3000,
        });
      }
    } catch (error: any) {
      console.error(error);
      toast.error(error.message || "Something went wrong", {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 3000,
      });
    }
  };

  return (
    <div className="p-5">
      <div className="block sm:flex items-center">
        <h1 className="text-xl font-bold px-3 py-3">Location Upload</h1>
        {/* <img src={guide} className="ml-auto mr-12 cursor-pointer" alt="guide" /> */}
      </div>
      <hr className="mt-4 border border-gray-200" />

      <>
        <div className="mt-10">
          <div className="flex justify-center mt-0">
            <div
              className={`border-2 border-dashed border-opacity-50 border-gray-400 ${
                uploadedData?.length === 0 ? `bg-white` : `bg-green-300`
              } flex flex-col justify-center items-center px-28 py-12`}
            >
              <input
                type="file"
                ref={fileInputRef}
                id="fileInput"
                accept=".csv, .xlsx"
                className="hidden"
                onChange={handleFileUpload}
              />
              <img
                src={uploadIcon}
                alt="Bulk Upload Img"
                className="cursor-pointer"
                onClick={() => document.getElementById("fileInput")?.click()}
              />
              {fileName !== "" && (
                <div>
                  <span className="text-wrap w-2">{fileName}</span>
                  <span
                    className="cursor-pointer text-2xl text-red-500 ml-2"
                    onClick={() => {
                      handleRemoveFile();
                    }}
                  >
                    x
                  </span>
                </div>
              )}

              <p
                id="inputName"
                className="text-[#009CA7] cursor-pointer font-semibold mt-4 text-base"
                onClick={() => {
                  handleRemoveFile();
                  document.getElementById("fileInput")?.click();
                }}
              >
                {uploadedData?.length === 0 ? "Upload File" : "Replace File"}
              </p>
            </div>
          </div>
        </div>
        <div className="flex justify-center items-center mt-7">
          <h1 className="text-base">
            Use csv format, or{" "}
            <a
              href="/location_template.xlsx"
              className="text-[#009CA7] font-bold"
              download
            >
              Download Template
            </a>
          </h1>
        </div>
      </>

      <div className="flex fixed bottom-6 left-16 right-0 justify-center items-center gap-10 sm:gap-96">
        <button
          className="flex px-6 py-1.5 border-2 border-grey-300 rounded-md bg-white mr-4"
          onClick={() => {
            history.push("/about");
          }}
        >
          Cancel
        </button>
        <button
          disabled={uploadedData?.length === 0}
          className={`flex px-8 py-1.5 border-2 border-grey-300 rounded-md text-white ${
            uploadedData?.length === 0 ? `bg-gray-400` : `bg-[#009CA7]`
          } `}
          onClick={() => handleSave(uploadedData)}
        >
          Save
        </button>
      </div>
    </div>
  );
}
